import { useDispatch, useSelector } from "react-redux";
import { BuilderProduct, BuilderRoomAsset, BuilderRoomAssets, Project, RoomModel, Surface, SurfaceGroup, Texture, TwoDMesh } from "@/store/builderSlice";
import { useGLTF, useTexture } from '@react-three/drei';
import { useEffect, useRef, useState } from "react";
import * as THREE from 'three';
import { MeshCollider, RigidBody } from "@react-three/rapier";
import BuilderSelct from "@/store/builderStoreSelector";
import { DimensionalViewType, ProductPlacementType, SurfaceType } from "@/utils/builder/builderConstants";
import { actions as builderActions } from "@/store/builderSlice";
import { getDefaultWallColor, getDefaultWallTexture } from "@/utils/builder/wallTextureUtils";
import Wall from "./Wall";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { getDefaultFloorTexture, loadGLTF } from "@/utils/builder/floorTextureUtils";
import { useThree } from "@react-three/fiber";
import { Edge, findInnerWalls } from "@/utils/builder/collisionUtils";

export interface DoorOrWindowMesh {
    mesh: THREE.Mesh | TwoDMesh;
    asset: {
        model: THREE.Group;
        offsets: RoomAssetOffsets;
    },
    meshMetada?: {
        [key: string]: string;
    }
}
export interface RoomAssetOffsets {
    w: number;
    h: number;
    x: number;
    y: number;
    z: number;
}

export interface WallsProps {
    roomModelLoaded: Function;
    toggleControls: Function;
    setFloorTexture: React.Dispatch<React.SetStateAction<THREE.Texture | undefined>>; // Corrected type
}

export const ROOM_ASSET_OFFSETS: {
    [key: string]: RoomAssetOffsets
} = {
    'door_Hinged': {
        w: -0.15,
        h: -0.04,
        x: 0,
        y: 0,
        z: -0.05
    },
    'door_French': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.06
    },
    'door_Pocket': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.07
    },
    'door_Opening Glass': {
        w: -0.04,
        h: -0.01,
        x: 0,
        y: 0,
        z: -0.05
    },
    'door_Double Folding': {
        w: -0.06,
        h: 0,
        x: 0,
        y: 0,
        z: 0.09
    },
    'door_Folding': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: 0.03
    },
    'door_Bypass': {
        w: -1,
        h: 0,
        x: 0,
        y: 0,
        z: -0.05
    },
    'door_Double Hinged': {
        w: 0,
        h: 0,
        x: 0.025,
        y: 0,
        z: 0.175
    },
    'door_Patio': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.05
    },
    'door_Double Pocket': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.07
    },
    'door_Swing': {
        w: -0.05,
        h: -0.01,
        x: 0,
        y: 0,
        z: -0.05
    },
    'door_Opening': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.07
    },
    'window_French': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.01
    },
    'window_Casement': {
        w: -0.06,
        h: 0,
        x: 0,
        y: 0,
        z: 0.22
    },
    'window_Sliding': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.05
    },
    'window_Awning': {
        w: 0,
        h: 0,
        x: -0.02,
        y: -0.055,
        z: 0.22,
    },
    'window_Fixed': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.02
    },
    'window_Hung': {
        w: 0,
        h: 0,
        x: 0,
        y: 0,
        z: -0.02
    },
    'window_Awning2': {
        w: 0,
        h: 0,
        x: -0.032,
        y: 0,
        z: 0.22,
    }
}

const Walls = (props: WallsProps) => {
    const [roomModel, setRoomModel] = useState<undefined | any>();
    const [roomAssetsLoaded, setRoomAssetsLoaded] = useState(false);
    const roomBoundingBox = useRef<undefined | THREE.Box3>();
    const roomProducts = useRef<THREE.Object3D[]>([]);
    const matchedObjectNamess = new Set();
    const alignmentAngle = useRef(0);
    const roomRotationX = useRef(0);
    const verticesMap = useRef<{ [key: string]: THREE.Vector3[] }>({});
    const scene = useThree((state) => state.scene);
    const wallsGroup = useRef<THREE.Group>(null);
    const floorMeshRef = useRef<THREE.Mesh | null>(null);
    const dispatch = useDispatch();
    const walls = useRef<JSX.Element[]>([]);
    const roomAssetTypesRef = useRef<string[]>([]);
    const roomAssetTypeModelsRef = useRef<{ [key: string]: THREE.Group }>({});
    const doorAndWindowMeshesRef = useRef<THREE.Mesh[]>([]);
    const wallMeshesRef = useRef<THREE.Mesh[]>([]);
    const floorGeometryRef = useRef<THREE.ShapeGeometry | null>(null);
    const wallColorMapRef = useRef<{ [key: string]: { color: string, dimensions: number[] } }>({});
    const windowTypeMapRef = useRef<{ [key: string]: string }>({});
    const doorTypeMapRef = useRef<{ [key: string]: string }>({});
    const isThinWallRef = useRef(false);
    const defaultDoorType = 'Hinged';
    const defaultWindowType = 'Hung';
    const userFurnitureDefaultColor = new THREE.Color(0.5, 0.5, 0.5);

    const roomModels: RoomModel[] = BuilderSelct.roomModels().models;
    const currentRoomId: string = BuilderSelct.activeSelection().roomId;
    const urlParams: { [key: string]: string } = BuilderSelct.ui().urlParams;
    const is2DView: boolean = BuilderSelct.ui().dimensionalView === DimensionalViewType.TWO_D;
    const roomAssets: BuilderRoomAssets = BuilderSelct.roomAssets();
    let currentRoomModel: RoomModel | undefined;
    // const [floorTexture, setFloorTexture] = useState<THREE.Texture | null>(null);

    let roomType = urlParams.roomType;
    const wallsOnlyGroup = useRef<THREE.Group | null>(null);
    const nonWallsGroup = useRef<THREE.Group | null>(null);

    if (currentRoomId) {
        currentRoomModel = roomModels.find(roomModel => roomModel.roomId === currentRoomId);
    }

    const isEditFlow = urlParams.projectId != null;
    const isShowcase = urlParams.showcase == 'true';

    const isInternalUser = window.localStorage.getItem('trafficType') === 'internal';

    const slope = (coor1: { x: number, y: number }, coor2: { x: number, y: number }) => (coor2.y - coor1.y) / (coor2.x - coor1.x);

    const areCollinear = (a: { x: number, y: number }, b: { x: number, y: number }, c: { x: number, y: number }) => {
        if (Math.abs(b.x - a.x) < 0.001 && Math.abs(c.x - a.x) < 0.001)
            return true;
        const abSlope = slope(a, b);
        const acSlope = slope(a, c);
        return Math.abs(abSlope - acSlope) < 0.001;
    };

    // Function to calculate the polar angle of a point with respect to the centroid
    const calculatePolarAngle = (point: THREE.Vector3, centroid: { x: number, y: number }) => {
        return Math.atan2(point.z - centroid.y, point.x - centroid.x);
    }
    function getEdge(wallName: string, points: THREE.Vector3[]) {
        if (points.length == 0) {
            return { name: wallName, start: { x: 0, y: 0 }, end: { x: 0, y: 0 } };
        }
        let minX = points[0].x;
        let maxX = points[0].x;
        let minY = points[0].z;
        let maxY = points[0].z;

        for (let i = 1; i < points.length; i++) {
            const point = points[i];
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minY = Math.min(minY, point.z);
            maxY = Math.max(maxY, point.z);
        }

        const startPoint = { x: minX, y: minY };
        const endPoint = { x: maxX, y: maxY };

        return { name: wallName, start: startPoint, end: endPoint };
    }

    const captureVertices = (mesh: any, center: THREE.Vector3) => {
        // const innerWallNames = ['Wall5', 'Wall7', 'Wall10'];
        // if (innerWallNames.includes(mesh.name)) return;

        const geometry = mesh.geometry;
        geometry.computeBoundingBox();
        const meshVertices = geometry.attributes.position.array;
        const vertices: THREE.Vector3[] = [];
        // Assuming the mesh has three components per vertex (x, y, z)
        for (let i = 0; i < meshVertices.length; i += 3) {
            const x = meshVertices[i];
            const y = meshVertices[i + 1];
            const z = meshVertices[i + 2];
            const vertex = new THREE.Vector3(x, y, z);
            const wVertex = mesh.localToWorld(vertex);
            wVertex.x = -center.x + wVertex.x;
            wVertex.y = -center.y + wVertex.y;
            wVertex.z = -center.z + wVertex.z;
            const isVertextExists = vertices.find(vertex =>
                vertex.x === wVertex.x &&
                vertex.y === wVertex.y &&
                vertex.z === wVertex.z) !== undefined;
            if (wVertex.y < -1 && !isVertextExists)
                vertices.push(wVertex);
        }


        const wPostion = new THREE.Vector3();
        mesh.getWorldPosition(wPostion);
        wPostion.x = -center.x + wPostion.x;
        wPostion.y = -center.y + wPostion.y;
        wPostion.z = -center.z + wPostion.z;
        // wPostion.y = vertices[0].y;

        const insideVertices: THREE.Vector3[] = [];
        for (let i = 0; i < vertices.length; i++) {
            for (let j = i + 1; j < vertices.length; j++) {
                if (areCollinear({ x: vertices[i].x, y: vertices[i].z }, { x: vertices[j].x, y: vertices[j].z }, { x: wPostion.x, y: wPostion.z })) {
                    let isVertextExists = insideVertices.find(vertex => vertex == vertices[i]) !== undefined;
                    if (!isVertextExists) insideVertices.push(vertices[i]);
                    isVertextExists = insideVertices.find(vertex => vertex == vertices[j]) !== undefined;
                    if (!isVertextExists) insideVertices.push(vertices[j]);
                }
            }
        }
        verticesMap.current[mesh.name] = insideVertices;
    }

    const findInsideWalls = () => {
        const edges: Edge[] = [];
        const wallNames = Object.keys(verticesMap.current);
        wallNames.forEach(wallName => {
            const curWallVertices = verticesMap.current[wallName];
            edges.push(getEdge(wallName, curWallVertices));
        });
        const insideWalls = findInnerWalls(edges);
        insideWalls.forEach(insideWall => {
            delete verticesMap.current[insideWall.name]
        });
    }

    const getSortedVerticesClockWise = (vertices: THREE.Vector3[]) => {
        const centroid = { x: 0, y: 0 };

        // Calculate polar angles between each point and the centroid
        const pointsWithAngles = vertices.map(point => {
            const angle = calculatePolarAngle(point, centroid);
            return { point, angle };
        });

        // Sort the points based on their polar angles in clockwise direction
        pointsWithAngles.sort((a, b) => a.angle - b.angle);

        // Extract the sorted points
        const sortedPoints = pointsWithAngles.map(({ point }) => {
            return new THREE.Vector3(-point.x, point.y, point.z);
        });
        return sortedPoints;
    }

    const getFloorGeometry = (center: THREE.Vector3) => {
        const insideVertices: THREE.Vector3[] = [];
        const excludedVertices: THREE.Vector3[] = [];
        const wallNames = Object.keys(verticesMap.current);
        for (let i = 0; i < wallNames.length; i++) {
            const curWallVertices = verticesMap.current[wallNames[i]];
            console.log(wallNames[i], ' :: processing.............', curWallVertices.length);
            curWallVertices.forEach(curVertex => {
                let vertexToAdd = curVertex;
                let vertexToExclude = null;
                for (let j = i + 1; j < wallNames.length; j++) {
                    const nextWallVertices = verticesMap.current[wallNames[j]];
                    nextWallVertices.forEach(nextVertex => {
                        const deltaX = Math.abs(curVertex.x - nextVertex.x);
                        const deltaZ = Math.abs(curVertex.z - nextVertex.z);
                        if (deltaX < 0.05) {
                            if (curVertex.z < 0 && nextVertex.z < 0 && deltaZ < 0.4) {
                                if (curVertex.z > nextVertex.z) {
                                    vertexToAdd = curVertex;
                                    vertexToExclude = nextVertex;
                                } else {
                                    vertexToAdd = nextVertex;
                                    vertexToExclude = curVertex;
                                }
                            }
                            if (curVertex.z > 0 && nextVertex.z > 0 && deltaZ < 0.4) {
                                if (curVertex.z < nextVertex.z) {
                                    vertexToAdd = curVertex;
                                    vertexToExclude = nextVertex;
                                } else {
                                    vertexToAdd = nextVertex;
                                    vertexToExclude = curVertex;
                                }
                            }
                        }
                        else if (deltaZ < 0.05) {
                            if (curVertex.x < 0 && nextVertex.x < 0 && deltaX < 0.4) {
                                if (curVertex.x > nextVertex.x) {
                                    vertexToAdd = curVertex;
                                    vertexToExclude = nextVertex;
                                } else {
                                    vertexToAdd = nextVertex;
                                    vertexToExclude = curVertex;
                                }
                            }
                            if (curVertex.x > 0 && nextVertex.x > 0 && deltaX < 0.4) {
                                if (curVertex.x < nextVertex.x) {
                                    vertexToAdd = curVertex;
                                    vertexToExclude = nextVertex;
                                } else {
                                    vertexToAdd = nextVertex;
                                    vertexToExclude = curVertex;
                                }
                            }
                        }
                    });
                }
                let isVertextExists = insideVertices.find(vertex => vertex == vertexToAdd) !== undefined;
                let isVertexExcluded = excludedVertices.find(vertex => vertex == vertexToAdd) !== undefined;
                if (!isVertextExists && !isVertexExcluded) {
                    insideVertices.push(vertexToAdd);
                    console.log(wallNames[i], ' :: adding vertex :: ', vertexToAdd);
                }
                if (vertexToExclude) {
                    excludedVertices.push(vertexToExclude);
                    console.log(wallNames[i], ' :: exclude vertex :: ', vertexToExclude);
                }
            });
        }

        console.log('insideVertices :: ', insideVertices, excludedVertices);
        const dotMaterial = new THREE.PointsMaterial({ size: 0.15, color: 0xff0000 });
        insideVertices.forEach(vertex => {
            const dotGeometry = new THREE.BufferGeometry();
            dotGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([center.x + vertex.x, center.y + vertex.y, center.z + vertex.z]), 3));
            const dot = new THREE.Points(dotGeometry, dotMaterial);
            //scene.add(dot);
        });

        const sortedVertices = getSortedVerticesClockWise(insideVertices);
        console.log('sortedPoints :: ', sortedVertices);
        const floorPolygon: { x: number, y: number }[] = [];
        sortedVertices.forEach(vertex => floorPolygon.push({ x: vertex.x, y: vertex.z }));
        dispatch(builderActions.updateFloorPolygon({
            polygon: floorPolygon,
        }));


        const roomShape = new THREE.Shape();
        roomShape.moveTo(sortedVertices[0].x, sortedVertices[0].z);
        for (let i = 1; i < sortedVertices.length; i++) {
            roomShape.lineTo(sortedVertices[i].x, sortedVertices[i].z);
        }
        const floorGeometry = new THREE.ShapeGeometry(roomShape);
        floorGeometry.rotateZ(Math.PI);
        return floorGeometry;
    }

    const addProductsFromRoom = (objs: THREE.Object3D[]) => {
        const METER_TO_INCHES_FACTOR = 39.3701;
        const products: BuilderProduct[] = [];
        const productPlacements: any[] = [];
        objs.forEach(obj => {
            const objBoundingBox = new THREE.Box3().setFromObject(obj);
            const objSize = new THREE.Vector3();
            objBoundingBox.getSize(objSize);
            const position = new THREE.Vector3(obj.position.x, obj.position.y, obj.position.z);
            obj.position.x = 0;
            obj.position.y = 0.4;
            obj.position.z = 0;
            const rotation = {
                x: obj.quaternion.x,
                y: obj.quaternion.y,
                z: obj.quaternion.z,
                w: obj.quaternion.w
            };
            obj.rotation.x = 0;
            obj.rotation.y = 0;
            obj.rotation.z = 0;

            products.push({
                productId: obj.name,
                productName: obj.name,
                productImage: '',
                productDesignImage: '',
                productDescription: '',
                productTypeName: '',
                isDefault: '',
                style: '',
                styleId: '',
                model3dUrl: '',
                price: 0,
                comparePrice: 0,
                productDimentions: {
                    depth: objSize.x * METER_TO_INCHES_FACTOR,
                    width: objSize.z * METER_TO_INCHES_FACTOR,
                    height: objSize.z * METER_TO_INCHES_FACTOR,
                    weight: 0
                },
                isFromRoomScan: true,
                object3D: obj
            } as BuilderProduct);

            productPlacements.push({
                productId: obj.name,
                //position: new THREE.Vector3(obj.position.x + 0.17864754691661966, obj.position.y + 0.6460497802810465, obj.position.z + 0.5299486297553355)
                position: position,
                rotation
            })
        });

        dispatch(builderActions.addProductData({
            products: products
        }));


        !isEditFlow && dispatch(builderActions.addRoomScanProductsToProject({
            placements: productPlacements,
            surfaceType: SurfaceType.FLOOR,
            surfaceId: SurfaceType.FLOOR,
            type: ProductPlacementType.Product,
        }));

    }
    const checkIfAllRoomAssetsLoaded = () => {
        if (roomAssetTypesRef.current.length === Object.keys(roomAssetTypeModelsRef.current).length) {
            wallMeshesRef.current.forEach(wallMesh => {
                wallMesh.geometry.computeBoundingBox();
                const wallBoundingBox = new THREE.Box3().setFromObject(wallMesh);
                // const doorAndWindowMeshes = doorAndWindowMeshesRef.current.filter(doorOrWindowMesh =>
                //     wallBoundingBox.containsPoint(doorOrWindowMesh.position)
                // );
                const doorAndWindowMeshes = doorAndWindowMeshesRef.current.filter(doorOrWindowMesh =>
                    wallMesh.parent?.name == doorOrWindowMesh.parent?.name
                );

                const doorMeshes: DoorOrWindowMesh[] = [];
                const windowMeshes: DoorOrWindowMesh[] = [];
                doorAndWindowMeshes.forEach(doorOrWindowMesh => {
                    doorOrWindowMesh.geometry.computeBoundingBox();
                    if (doorOrWindowMesh.name.indexOf('Window') !== -1) {
                        const type = windowTypeMapRef.current[doorOrWindowMesh.name];
                        let assetModel = roomAssetTypeModelsRef.current['window_' + type];
                        let windowMetadata;
                        if (currentRoomModel && currentRoomModel.data && currentRoomModel.data.windows) {
                            windowMetadata = currentRoomModel.data.windows.find((windowMetadata) => windowMetadata.identifier === doorOrWindowMesh.name);
                        }
                        if (assetModel && windowMetadata)
                            windowMeshes.push({
                                mesh: doorOrWindowMesh,
                                asset: {
                                    model: assetModel,
                                    offsets: ROOM_ASSET_OFFSETS['window_' + type]
                                },
                                meshMetada: windowMetadata
                            });
                        else if (windowMetadata) {
                            assetModel = roomAssetTypeModelsRef.current['window_' + defaultWindowType];
                            windowMeshes.push({
                                mesh: doorOrWindowMesh,
                                asset: {
                                    model: assetModel,
                                    offsets: ROOM_ASSET_OFFSETS['window_' + defaultWindowType]
                                },
                                meshMetada: windowMetadata
                            });
                        }
                    }

                    if (doorOrWindowMesh.name.indexOf('Door') !== -1) {
                        const type = doorTypeMapRef.current[doorOrWindowMesh.name];
                        let assetModel = roomAssetTypeModelsRef.current['door_' + type];
                        let doorMetadata;
                        if (currentRoomModel && currentRoomModel.data && currentRoomModel.data.doors) {
                            doorMetadata = currentRoomModel.data.doors.find((doorMetadata) => doorMetadata.identifier === doorOrWindowMesh.name);
                        }
                        if (assetModel && doorMetadata)
                            doorMeshes.push({
                                mesh: doorOrWindowMesh,
                                asset: {
                                    model: assetModel,
                                    offsets: ROOM_ASSET_OFFSETS['door_' + type]
                                },
                                meshMetada: doorMetadata
                            });
                        else if (doorMetadata) {
                            assetModel = roomAssetTypeModelsRef.current['door_' + defaultDoorType];
                            doorMeshes.push({
                                mesh: doorOrWindowMesh,
                                asset: {
                                    model: assetModel,
                                    offsets: ROOM_ASSET_OFFSETS['door_' + defaultDoorType]
                                },
                                meshMetada: doorMetadata
                            });
                        }
                    }
                });
                walls.current.push(<Wall
                    wallMesh={wallMesh}
                    doorMeshes={doorMeshes}
                    windowMeshes={windowMeshes}
                    isThinWall={isThinWallRef.current}
                    isWallRotated={alignmentAngle.current !== 0}
                    wallDimensions={wallColorMapRef.current[wallMesh.name] && wallColorMapRef.current[wallMesh.name].dimensions}
                    hasFurnitureModels={!!(currentRoomModel && currentRoomModel.data && currentRoomModel.data.hasFurnitureModelsInUSDZ as unknown as boolean)}
                />);
            });
            dispatch(builderActions.updateResourceLoadStatus({
                name: currentRoomModel!.roomId,
                status: 'Success',
            }));
            addProductsFromRoom(roomProducts.current);
            props.roomModelLoaded(roomBoundingBox.current,
                floorMeshRef.current ? (floorMeshRef.current as THREE.Mesh).geometry : floorGeometryRef.current,
                floorMeshRef.current);
            setRoomAssetsLoaded(true);
        }
    }
    const onRoomAssetLoaded = (assetType: string, gltf: GLTF) => {
        const scene = gltf.scene;
        scene.traverse((child: any) => {
            if (child.isMesh && child.material) {
                (child.material as THREE.MeshBasicMaterial).color = new THREE.Color('#FFFFFF');
                (child.material as THREE.MeshBasicMaterial).needsUpdate = true;
            }
        })
        roomAssetTypeModelsRef.current[assetType] = scene;
        checkIfAllRoomAssetsLoaded();
    }

    const loadRoomAssetModels = (assetTypes: string[], assets: BuilderRoomAsset[], category: string) => {
        assetTypes.forEach(assetType => {
            const roomAsset = assets.find(roomAsset =>
                roomAsset.type === assetType && roomAsset.threeDimentionalUrl != null
            );
            if (roomAsset) {
                roomAssetTypesRef.current.push(assetType);
                loadGLTF(roomAsset.threeDimentionalUrl, onRoomAssetLoaded.bind(this, category + '_' + roomAsset.type));
            }
        });
    }

    const controlShadowCasting = (child: any)=> {
        if(child.isMesh) {
            // Default to casting shadows
            let shouldCastShadow = true;
            
            // Handle material transparency based on both opacity and texture alpha
            if (Array.isArray(child.material)) {
                // Check each material in the array
                for (const mat of child.material) {
                    // Skip if already decided not to cast shadows
                    if (!shouldCastShadow) break;
                    
                    // Direct opacity check
                    if (mat.transparent && typeof mat.opacity === 'number' && mat.opacity < 0.5) {
                        shouldCastShadow = false;
                        break;
                    }
                    
                    // Alpha map check
                    if (mat.alphaMap && mat.transparent) {
                        shouldCastShadow = false;
                        break;
                    }
                    
                    // Texture with alpha channel
                    if (mat.map && mat.transparent) {
                        shouldCastShadow = false;
                        break;
                    }
                    
                    // Alpha test check
                    if (mat.alphaTest > 0 && mat.transparent) {
                        shouldCastShadow = false;
                        break;
                    }
                }
            } else if (child.material) {
                // Single material case
                const mat = child.material;
                
                // Direct opacity check
                if (mat.transparent && typeof mat.opacity === 'number' && mat.opacity < 0.5) {
                    shouldCastShadow = false;
                }
                
                // Alpha map check
                else if (mat.alphaMap && mat.transparent) {
                    shouldCastShadow = false;
                }
                
                // Texture with alpha channel
                else if (mat.map && mat.transparent) {
                    shouldCastShadow = false;
                }
                
                // Alpha test check
                else if (mat.alphaTest > 0 && mat.transparent) {
                    shouldCastShadow = false;
                }
            }
            
            // Name-based checks for typical transparent objects
            const name = child.name.toLowerCase();
            if (name.includes('glass') || 
                name.includes('window') || 
                name.includes('transparent') ||
                name.includes('curtain')) {
                shouldCastShadow = false;
            }
            
            // Apply shadow settings
            child.castShadow = shouldCastShadow;
            child.receiveShadow = true; // Always receive shadows
            
            // Debug output for transparent materials
            // @ts-ignore
            if (child.material && ((Array.isArray(child.material) && child.material.some(m => m.transparent)) ||
                 (!Array.isArray(child.material) && child.material.transparent))) {
                
                console.log(`Object ${child.name}: transparent material detected, castShadow=${child.castShadow}`);
            }
        }  
    }
    const onRoomModelLoaded = (gltf: GLTF) => {
        let roomMesh: any;
        const wallsMap: { [key: string]: THREE.Mesh[] } = {};
        
        // Create two groups for separation
        const wallsGroup = new THREE.Group();
        wallsGroup.name = "WallsOnlyGroup";
        
        const otherObjectsGroup = new THREE.Group();
        otherObjectsGroup.name = "NonWallsGroup";
    
        // Process metadata first (unchanged)
        if (currentRoomModel && currentRoomModel.data && currentRoomModel.data.walls) {
            currentRoomModel.data.walls.forEach(wallMetadata => {
                wallColorMapRef.current[wallMetadata.identifier] = {
                    color: wallMetadata.color,
                    dimensions: wallMetadata.dimensions as unknown as number[]
                };
            });
        }
    
        if (currentRoomModel && currentRoomModel.data && currentRoomModel.data.doors) {
            currentRoomModel.data.doors.forEach((doorMetadata, index) => {
                doorMetadata.type && (doorTypeMapRef.current[doorMetadata.identifier] = doorMetadata.type);
            });
        }
    
        if (currentRoomModel && currentRoomModel.data && currentRoomModel.data.windows) {
            currentRoomModel.data.windows.forEach((windowMetadata, index) => {
                let windowType = windowMetadata.type;
                windowType && (windowTypeMapRef.current[windowMetadata.identifier] = windowType);
            });
        }
    
        doorTypeMapRef.current['default_door_type'] = defaultDoorType;
        windowTypeMapRef.current['default_window_type'] = defaultWindowType;
    
        let doorTypes = Array.from(new Set(Object.values(doorTypeMapRef.current)));
        let windowTypes = Array.from(new Set(Object.values(windowTypeMapRef.current)));
    
        // First pass - collect door and window meshes
        gltf.scene.traverse((child: any) => {
            if(child.isMesh) {
                controlShadowCasting(child)

            }
            if (child.isMesh && child.material &&
                (child.name.indexOf('Window') != -1 ||
                 child.name.indexOf('Door') != -1)) {
                doorAndWindowMeshesRef.current.push(child);
            }
        });
    
        let scannedItems: string[] = [];
    
        // IMPORTANT: We need to create a deep clone of the scene to preserve the original
        const clonedScene = gltf.scene.clone(true);
        
        // Second pass - process objects and identify walls
        gltf.scene.traverse((child: any) => {
            if (!roomMesh && child.name && child.type !== 'Mesh' && child.name.toLowerCase().indexOf('room') !== -1) {
                roomMesh = child;
            }
            
            if (child.name === 'Wall0') {
                const rotationZ = child.rotation.z;
                alignmentAngle.current = Math.abs(rotationZ);
                if (rotationZ > Math.PI / 2)
                    alignmentAngle.current = Math.abs(Math.PI - rotationZ);
            }
    
            // Process ceiling
            if (child.isMesh && child.name.toLowerCase() === "ceiling") {
                child.visible = false; // Hide the ceiling
            }
            
            // Process walls - identify but don't modify the original objects yet
            if (child.isMesh && child.material && /^Wall\d+$/.test(child.name) && !child.name.includes('attachment')) {
                const wallColor = wallColorMapRef.current[child.name]?.color || getDefaultWallColor();
                
                if (!isEditFlow) {
                    dispatch(builderActions.addWallTextureColorContent({
                        texture: {
                            id: wallColor,
                            name: wallColor,
                            hexCode: wallColor,
                            type: 'color',
                            surfaceType: 'Wall'
                        }
                    }));
                    dispatch(builderActions.addProductToProject({
                        productId: wallColor,
                        surfaceType: SurfaceType.WALL,
                        surfaceId: child.name,
                        type: ProductPlacementType.Color,
                    }));
                }
                
                isThinWallRef.current = child.scale.y < 1;
                isThinWallRef.current && (child.scale.y = 0.02);
                updateUVMap(child.geometry);
                wallMeshesRef.current.push(child);
            }
    
            // Process floor
            if (child.isMesh && child.material && child.name.indexOf('Floor') !== -1 && child.name == 'Floor_hidden_drag_drop') {
                updateUVMap(child.geometry);
                floorMeshRef.current = child as THREE.Mesh;
    
                const material = child.material as THREE.MeshStandardMaterial;
                if (material.map) {
                    console.log("✅ Keeping original GLB floor texture:", material.map);
                    floorMeshRef.current.material = material;
    
                    if (material.map.image) {
                        props.setFloorTexture(material.map);
                    }
                } else {
                    console.log("⚠️ No texture found, applying default material.");
                }
            }
    
            if (child.name === 'Floor_hidden_drag_drop') {
                child.visible = false;
                if (child.material) {
                    child.material.visible = false;
                    child.material.opacity = 0;
                    child.material.transparent = true;
                }
            }
    
            // Process furniture
            if (child.name.match(/^Sofa\d+$/) || 
                child.name.match(/^Table\d+$/) || 
                child.name.match(/^Chair\d+$/) || 
                child.name.match(/^Bed\d+$/)) {
                child.traverse((grandChild: any) => {
                    if (grandChild.isMesh) {
                        grandChild.material.color = userFurnitureDefaultColor;
                        grandChild.castShadow = true;
                        
                        if (currentRoomModel?.data.otherObjects) {
                            for (const obj of currentRoomModel?.data.otherObjects) {
                                if (matchedObjectNamess.has(obj.modelName)) {
                                    continue;
                                }
    
                                const categoryKey = Object.keys(obj.category)[0];
                                if (child.name.match(new RegExp(`^${categoryKey}\\d+$`, 'i'))) {
                                    if (obj.modelName) {
                                        grandChild.name = obj.modelName;
                                        scannedItems.push(grandChild.name);
                                    }
                                    matchedObjectNamess.add(obj.modelName);
                                    break;
                                }
                            }
                        }
                    }
                });
                roomProducts.current.push(child);
            }
        });
        
        localStorage.setItem(`scannedItems1_${urlParams.projectId}`, JSON.stringify(scannedItems));
    
        // Now properly distribute objects into separate groups for rendering
        clonedScene.traverse((child: any) => {
            if (child.isMesh && child.material && /^Wall\d+$/.test(child.name) && !child.name.includes('attachment')) {
                // Add wall objects to the walls group
                wallsGroup.add(child.clone());
            } else if (child.isMesh || child.isGroup) {
                // Add all non-wall objects to the other group
                // But only add top-level objects (those directly under the scene)
                if (child.parent === clonedScene) {
                    otherObjectsGroup.add(child.clone());
                }
            }
        });
    
        if (roomMesh) {
            roomRotationX.current = roomMesh.rotation.x;
            roomMesh.rotation.z = alignmentAngle.current;
        } else {
            alignmentAngle.current = 0;
        }
    
        console.log('debug- room model :: ', gltf, wallsMap);
    
        const box = new THREE.Box3().setFromObject(gltf.scene);
        const center = new THREE.Vector3();
        box.getCenter(center);
    
        // Store the groups for rendering
        wallsOnlyGroup.current = wallsGroup;
        nonWallsGroup.current = otherObjectsGroup;
    
        // Capture vertices for floor geometry
        gltf.scene.traverse((child: any) => {
            if (child.isMesh && child.material) {
                if (/^Wall\d+$/.test(child.name) && !child.name.includes('attachment')) {
                    captureVertices(child, center);
                }
            }
        });
    
        findInsideWalls();
        // floorGeometryRef.current = getFloorGeometry(center);
        
        // @ts-ignore
        floorGeometryRef.current = floorMeshRef;
    
        dispatch(builderActions.updateRoomBoundingBox({
            roomBoundingBox: box
        }));
        
        !isEditFlow && dispatch(builderActions.addProductToProject({
            productId: getDefaultFloorTexture(),
            surfaceType: SurfaceType.FLOOR,
            surfaceId: SurfaceType.FLOOR,
            type: ProductPlacementType.Texture
        }));
        
        roomBoundingBox.current = box;
        loadRoomAssetModels(doorTypes, roomAssets.doorTypes, 'door');
        loadRoomAssetModels(windowTypes, roomAssets.windowTypes, 'window');
        setRoomModel(gltf);
    }
    const updateUVMap = (geometry: any) => {
        const uvAttribute = geometry.getAttribute('uv');
        if (!uvAttribute) {
            // Calculate the UV coordinates for the buffer geometry
            const positions = geometry.getAttribute('position').array;
            const numVertices = positions.length / 3;
            const uvs = new Float32Array(numVertices * 2);
            for (let i = 0; i < numVertices; i++) {
                const x = positions[i * 3];
                const z = positions[i * 3 + 1];
                uvs[i * 2] = x / 10; // Scale the UV coordinates to fit within a 0-1 range
                uvs[i * 2 + 1] = z / 10;
            }
            geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
        } else {
            uvAttribute.needsUpdate = true;
        }
    }

    useEffect(() => {
        if (currentRoomModel) {
            dispatch(builderActions.updateResourceLoadStatus({
                name: currentRoomModel.roomId,
                status: 'InProgress',
            }));
            
            const loadRoomModel = async () => {
                const originalFilename:any = currentRoomModel?.glbUrl;
                const furnitureFilename = originalFilename?.replace(".glb", "_furniture.glb");
    
                const checkFileAvailability = async (url: any) => {
                    try {
                        const response = await fetch(url, { method: "HEAD" });
                        return response.ok;
                    } catch (error) {
                        console.error("Error checking file availability:", error);
                        return false;
                    }
                };
    
                const glbFileNameToLoad = (await checkFileAvailability(furnitureFilename))
                    ? furnitureFilename
                    : originalFilename;
    
                loadGLTF(glbFileNameToLoad, onRoomModelLoaded);
            };
    
            // if(isShowcase) {

            //     // loadGLTF('./sample_rooms/living_room/living_room.glb', onRoomModelLoaded);
            //     // loadGLTF('./sample_rooms/office/home_office.glb', onRoomModelLoaded);
            //     loadGLTF('./sample_rooms/with_balcony/with_balcony.glb', onRoomModelLoaded);

            // } else {
            //     loadRoomModel();
            // }

            if (isShowcase) {
                const roomTypeToGLTFMap: { [key: string]: string } = {
                    bedroom: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/bedroom/bedroom.glb',
                    bedroom1: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/bedroom/bedroom.glb',
                    bedroom2: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/bedroom/bedroom2.glb',

                    diningroom: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/dining_room/dining_room.glb',
                    diningroom1: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/dining_room/dining_room.glb',

                    livingroom: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/living_room/living_room.glb',
                    livingroom1: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/living_room/living_room.glb',
                    livingroom2: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/living_room/living_room2.glb',

                    office: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/office/home_office.glb',
                    office1: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/office/home_office.glb',
                    office2: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/office/home_office2.glb',

                    balcony: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/with_balcony/with_balcony.glb',


                    // coming soon...
                    bathroom: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/bathroom/bathroom.glb',
                    kitchen: 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/kitchen/kitchen.glb'
                };
                
                const normalizedRoomType = roomType?.replace(/\s+/g, '').toLowerCase();
                // const gltfPath = "./sample_rooms/with_balcony/with_balcony.glb"; // working
                const gltfPath = roomTypeToGLTFMap[normalizedRoomType] || 'https://d221a61rb87vel.cloudfront.net/designImages/sample_rooms/with_balcony/with_balcony.glb';
                console.log(gltfPath, 'gltfPath:');
            
                loadGLTF(gltfPath, onRoomModelLoaded);
            } else {
                loadRoomModel();
            }

            
            // loadRoomModel();

            // loadGLTF('https://d221a61rb87vel.cloudfront.net/upload3DmodelData/temp/Bedroom1.glb', onRoomModelLoaded);

            // loadGLTF('./floors/sample1_yUpTrue.glb', onRoomModelLoaded);

            // loadGLTF('./floors/sample_room_merged_attachments.glb', onRoomModelLoaded);

            // loadGLTF('./floors/NewRoom_4K_3.glb', onRoomModelLoaded);
        }
    }, [currentRoomModel]);

    if (!roomModel || !roomBoundingBox.current || !roomAssetsLoaded) return <></>;

    // console.log('debug- floorMeshRef:: ', floorMeshRef.current, roomBoundingBox.current.min.y, -(roomBoundingBox.current.max.y - roomBoundingBox.current.min.y) / 2)
    const wallsPositionY = is2DView ?
        -(roomBoundingBox.current.max.y - roomBoundingBox.current.min.y) / 2 +
        (floorMeshRef.current ? (floorMeshRef.current.position.y - roomBoundingBox.current.min.y) : 0) :
        0;
    return (
        <>

        <RigidBody
            name={'walls'}
            type="fixed"
            colliders={isShowcase ? "hull" : false}
            userData={{ type: 'walls' }}
        >
            {wallsOnlyGroup.current && 
                <group position={[0, wallsPositionY, 0]}>
                    <primitive object={wallsOnlyGroup.current} />
                </group>
            }
        </RigidBody>
        
        {/* Everything else outside of RigidBody */}
        {nonWallsGroup.current && 
            <group position={[0, wallsPositionY, 0]}>
                <primitive object={nonWallsGroup.current} />
            </group>
        }
        </>

    )
}

export default Walls;